/* eslint-disable max-len */
import { Card, Col, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { IAmlz } from 'types/amlz';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import fileDownload from 'services/fileDownload';
import { historiePDFdownloadApi } from 'services/spravaEmitenta/amlzData';
import historickeZadostiApi, { EStavZadostAmlz, IZadosti } from 'services/spravaEmitenta/historickeZadosti';
import vyjadreniCdcpApi from 'services/spravaEmitenta/vyjadreniCdcp';
import {
    EPristupStav,
    zadostPravoCheck,
    zadostPravoVyjadreni,
    zadostPravoZrusit
} from 'services/spravaEmitenta/zadostPravo';
import zrusitZadost from 'services/spravaEmitenta/zrusitZadost';

import { ENotificationType } from 'enums/notificationType';
import routePathEnum from 'enums/routePathEnum';

interface IPrehledEmitent {}

const PrehledEmitent: React.FC<IPrehledEmitent> = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { aktivniFirma, amlz, NotificationComponent, menuProfile, zadostAgenda } = useContext(Context);
    const { setLevel } = useProfily();
    const [prehled, setPrehled] = useState<IZadosti | undefined>();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [refetchData, setRefetchData] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [modalVyjadreni, setModalVyjadreni] = useState<string>('');
    const isDlt = window.location.href.includes('sprava-pravnicke-osoby');

    useEffect(() => {
        if (isDlt && !menuProfile?.crsHotovo) return setErrStatus(404);
        setLoading(true);
        setErrStatus(0);

        if (menuProfile?.amlz === 'ZADOST') {
            zadostPravoCheck()
                .then(resp => {
                    setLevel('zadostAgenda', resp);
                    setLoading(false);
                    if (resp.pristupamlz?.stav === EPristupStav.KDOPL) {
                        zadostPravoVyjadreni(zadostAgenda?.pristupamlz?.id || resp.pristupamlz.id).then(data =>
                            setModalVyjadreni(data.vyjadreni)
                        );
                    }
                })
                .catch((err: any) => {
                    setErrStatus(err.response?.status);
                })
                .finally(() => setLoading(false));
        } else {
            menuProfile?.amlz !== 'OSOBY' &&
                historickeZadostiApi({
                    offset: 0,
                    order: 'datum:desc',
                    limit: 10
                })
                    .then(resp => {
                        resp.zadost
                            ? setLevel('amlz', {
                                  ...amlz,
                                  ...resp.zadost,
                                  ...(resp.lastid && {
                                      idPoslednejHotovejZadosti: resp.lastid
                                  })
                              } as IAmlz)
                            : menuProfile?.amlz === 'NOVY' && (setLevel('amlz', null), setErrStatus(404));
                        setPrehled(resp.zadost || undefined);

                        if (
                            resp.zadosti?.length &&
                            (resp.zadost?.stav === EStavZadostAmlz.KDOPL ||
                                resp.zadost?.stav === EStavZadostAmlz.CDCPKDOPL)
                        ) {
                            vyjadreniCdcpApi(resp.zadost.idzadosti).then(data => setModalVyjadreni(data.vyjadreni));
                        }
                    })
                    .catch((err: any) => {
                        setLevel('amlz', null);
                        setErrStatus(err.response?.status);
                    })
                    .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetchData]);

    return (
        <LoadingWrapper
            loading={loading}
            style={{
                paddingTop: '100px'
            }}
        >
            {errStatus ? (
                <Row
                    justify="center"
                    align="middle"
                    style={{
                        height: '100%'
                    }}
                >
                    <Col span={16} flex={'0 0 auto'}>
                        {errStatus === 404 ? (
                            <Title
                                text={isDlt && !menuProfile?.crsHotovo ? 'CRS_AML_ERR' : 'CHYBA_AMLZ_NEVEDENO'}
                                level={3}
                            />
                        ) : errStatus ? (
                            <Title text="SLUZBA_NEDOSTUPNA" level={3} />
                        ) : null}
                    </Col>
                </Row>
            ) : menuProfile?.amlz !== 'NOVY' &&
              menuProfile?.amlz !== 'EDITOVAT' &&
              zadostAgenda?.pristupamlz?.stav === 'ZADNA' ? (
                <>
                    <Title level={3} text="AMLZ_ZADOST_MENU" />
                    <Paragraph
                        style={{
                            display: 'block',
                            maxWidth: 800
                        }}
                        text="AMLZ_ZADOST_TEXT"
                    />
                </>
            ) : menuProfile?.amlz === 'ZADOST' ? (
                <>
                    {' '}
                    <Row gutter={16}>
                        <Col
                            span={12}
                            style={{
                                marginBottom: 36
                            }}
                        >
                            <Title text="EMITENT_ZASTUPUJICI_OSOBY_ROZP" level={4} />
                            <Card className="dash-card" title={''}>
                                <Title text="AMLZ_PREHLED_DATUM" level={5} />
                                <p>{dayjs(zadostAgenda?.pristupamlz?.datumzmeny).format('DD.MM.YYYY')}</p>
                                {zadostAgenda?.pristupamlz?.stav && (
                                    <>
                                        <Title text="EMP_PODANE_ZADOSTI_STAV" level={5} />

                                        <p>
                                            <TranslateText>
                                                {`STAVZADOSTI_SIMPLE_${(zadostAgenda?.pristupamlz?.stav || '')
                                                    .toUpperCase()
                                                    .split(' ')
                                                    .join('_')}`}
                                            </TranslateText>
                                        </p>
                                    </>
                                )}
                                <Title text="AMLZ_PREHLED_DATUM" level={5} />
                                <p>{dayjs(zadostAgenda?.pristupamlz?.datumzmeny).format('DD.MM.YYYY')}</p>
                                {menuProfile.datplatamlz && (
                                    <div
                                        style={{
                                            marginBottom: 24
                                        }}
                                    >
                                        <Title text="DATUM_PLATNOSTI_AML" level={5} />
                                        <Paragraph
                                            text={dayjs(menuProfile.datplatamlz).format('DD.MM.YYYY')}
                                            className={
                                                dayjs(menuProfile.datplatamlz) < dayjs().add(30, 'day')
                                                    ? 'extramessage-text'
                                                    : ''
                                            }
                                        />
                                    </div>
                                )}
                                <Row className="button-row">
                                    {(zadostAgenda?.pristupamlz?.stav === EPristupStav.ROZP ||
                                        zadostAgenda?.pristupamlz?.stav === EPristupStav.KDOPL ||
                                        prehled?.stav === EStavZadostAmlz.CDCPKDOPL ||
                                        prehled?.stav === EStavZadostAmlz.DOPL ||
                                        prehled?.stav === EStavZadostAmlz.ROZP) && (
                                        <>
                                            <ButtonComponent
                                                buttontext={'POKRACOVAT'}
                                                id="emitentPrehledPokracovatBtn"
                                                onClick={() => {
                                                    navigateWorkaround(
                                                        `/firma/${aktivniFirma?.iducet}${routePathEnum.AMLZ_ZADOST_O_AGENDU}`
                                                    );
                                                }}
                                            />

                                            <ButtonComponent
                                                id="emitentPrehledZrusitZadostBtn"
                                                buttontext={'ZRUSIT_ZADOST'}
                                                onClick={() => {
                                                    setLoading(true);
                                                    zadostPravoZrusit(zadostAgenda?.pristupamlz?.id || 0).then(() => {
                                                        NotificationComponent({
                                                            title: 'ZRUSIT_ZADOST',
                                                            description: 'ZRUSIT_ZADOST_SUCCESS',
                                                            type: ENotificationType.SUCCESS
                                                        });
                                                        setRefetchData(pv => !pv);
                                                    });
                                                }}
                                            />
                                        </>
                                    )}

                                    {(zadostAgenda?.pristupamlz?.stav === EPristupStav.KDOPL ||
                                        prehled?.stav === EStavZadostAmlz.CDCPKDOPL ||
                                        prehled?.stav === EStavZadostAmlz.DOPL) &&
                                        modalVyjadreni && (
                                            <ButtonComponent
                                                buttontext={'VYJADRENI_CDCP'}
                                                onClick={() => {
                                                    setModalOpen(true);
                                                }}
                                                id="emitentPrehledVyjadreniBtn"
                                            />
                                        )}
                                </Row>
                            </Card>
                        </Col>
                    </Row>
                </>
            ) : (
                <>
                    <Title level={3} text="AMLZ_PREHLED_NADPIS" />
                    <Row gutter={8}>
                        <Col
                            span={12}
                            style={{
                                marginBottom: 16
                            }}
                        >
                            {[prehled]?.map((zadost, i) => {
                                return (
                                    <Card
                                        key={i}
                                        className="dash-card"
                                        title={<TranslateText>ZADOST_O_AMLZ</TranslateText>}
                                    >
                                        <Title text="AMLZ_PREHLED_DATUM" level={5} />
                                        <p>{dayjs(zadost?.datum).format(' DD.MM.YYYY')}</p>
                                        <Title text="EMP_PODANE_ZADOSTI_STAV" level={5} />
                                        <p>
                                            <TranslateText>
                                                {`STAVZADOSTI_SIMPLE_${zadost
                                                    ?.stav!.toUpperCase()
                                                    .split(' ')
                                                    .join('_')}`}
                                            </TranslateText>
                                        </p>
                                        <Row
                                            className="button-row"
                                            style={{
                                                position: 'relative',
                                                top: 15
                                            }}
                                        >
                                            {(zadost?.stav === EStavZadostAmlz.HOTOVO ||
                                                zadost?.stav === EStavZadostAmlz.HOTOVOIIS) && (
                                                <ButtonComponent
                                                    id="emitentPrehledStahnoutPdfBtn"
                                                    buttontext={'STAHNOUT_PDF'}
                                                    onClick={() => {
                                                        setLoading(true);
                                                        historiePDFdownloadApi(zadost.idzadosti).then(data => {
                                                            if (data.redirect) {
                                                                fileDownload(
                                                                    data.redirect,
                                                                    `${zadost.idzadosti}_ZadostAMLZ.pdf`
                                                                ).finally(() => setLoading(false));
                                                            }
                                                        });
                                                    }}
                                                />
                                            )}
                                            {(zadost?.stav === EStavZadostAmlz.ROZP ||
                                                zadost?.stav === EStavZadostAmlz.KDOPL ||
                                                prehled?.stav === EStavZadostAmlz.CDCPKDOPL ||
                                                prehled?.stav === EStavZadostAmlz.DOPL) &&
                                                amlz?.idzadosti &&
                                                zadost && (
                                                    <>
                                                        <AnchorComponent
                                                            link={true}
                                                            to={
                                                                aktivniFirma
                                                                    ? // eslint-disable-next-line max-len
                                                                      `/firma/${aktivniFirma?.iducet}${routePathEnum.AMLZ_FORMULAR}`
                                                                    : ''
                                                            }
                                                            onClick={() =>
                                                                setLevel('amlz', {
                                                                    ...amlz,
                                                                    sprava: modalVyjadreni
                                                                })
                                                            }
                                                        >
                                                            <ButtonComponent
                                                                buttontext={'POKRACOVAT'}
                                                                id="emitentPrehledPokracovatBtn"
                                                            />
                                                        </AnchorComponent>
                                                        <ButtonComponent
                                                            id="emitentPrehledZrusitZadostBtn"
                                                            buttontext={'ZRUSIT_ZADOST'}
                                                            onClick={() => {
                                                                setLoading(true);
                                                                zrusitZadost(zadost.idzadosti)
                                                                    .then(() => {
                                                                        setRefetchData(val => !val);
                                                                        NotificationComponent({
                                                                            title: 'ZRUSIT_ZADOST',
                                                                            description: 'ZRUSIT_ZADOST_SUCCESS',
                                                                            type: ENotificationType.SUCCESS
                                                                        });
                                                                    })
                                                                    .finally(() => setLoading(false));
                                                            }}
                                                        />
                                                    </>
                                                )}
                                            {(zadost?.stav === EStavZadostAmlz.KDOPL ||
                                                zadost?.stav === EStavZadostAmlz.CDCPKDOPL ||
                                                zadost?.stav === EStavZadostAmlz.DOPL) &&
                                                modalVyjadreni && (
                                                    <ButtonComponent
                                                        buttontext={'VYJADRENI_CDCP'}
                                                        onClick={() => {
                                                            setModalOpen(true);
                                                        }}
                                                        id="emitentPrehledVyjadreniBtn"
                                                    />
                                                )}
                                        </Row>
                                    </Card>
                                );
                            })}
                        </Col>
                    </Row>
                </>
            )}
            <Modal
                title={<TranslateText>{'VYJADRENI_CDCP'}</TranslateText>}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                afterClose={() => {
                    setModalOpen(false);
                }}
                footer={
                    <Row className="button-row-modal">
                        <ButtonComponent
                            buttontext="ZAVRIT"
                            onClick={() => {
                                setModalOpen(false);
                            }}
                            id="emitentPrehledZavritBtn"
                        />
                    </Row>
                }
            >
                <Row>
                    <Paragraph text={modalVyjadreni} />
                </Row>
            </Modal>
        </LoadingWrapper>
    );
};

export default PrehledEmitent;
